import { couponBenefitHeadline, type CouponBenefit } from "@/lib/coupon-benefit"
import type { CouponTerms } from "@/lib/coupon-readiness"
import type { CatalogRef } from "@/lib/discount-evaluation/types"
import { refMatchesCartLine } from "@/lib/free-product-cart-item"
import type { CartItem, Category, Item, ItemOffer, OptionGroup } from "@/types"

const MAX_CHOICES = 24

/** One menu product a free-item prize can be spent on; `needsOptions` means it has a required choice, so it opens the product sheet first. */
export type PrizeChoice = { item: Item; offer: ItemOffer; needsOptions: boolean }

/**
 * How the customer spends a prize, by what its coupon gives and what the menu
 * offers right now: `use` attaches it to the order (discount, free delivery),
 * `add` puts the one eligible product in the bag, `pick` lets the customer
 * choose among several, and `menu` sends an item prize whose products are off
 * the menu right now to browse.
 */
export type PrizeOffer =
  | { kind: "use" }
  | { kind: "add"; choice: PrizeChoice }
  | { kind: "pick"; choices: PrizeChoice[] }
  | { kind: "menu" }

/** The catalog slice a prize offer is resolved against: the products and what is on the menu right now. */
export type PrizeCatalog = {
  items: readonly Item[]
  optionGroups: readonly OptionGroup[]
  visibleItemOffers: readonly ItemOffer[]
  visibleCategories: readonly Category[]
}

function needsOptions(offer: ItemOffer, optionGroups: readonly OptionGroup[]): boolean {
  const ids = offer.optionGroupsId ?? []
  return optionGroups.some((group) => ids.includes(group.id) && group.minPermitted > 0)
}

function offersFor(ref: CatalogRef, catalog: PrizeCatalog, onMenu: ReadonlySet<string>): ItemOffer[] {
  if (ref.kind === "REF_KIND_CATEGORY") {
    const category = catalog.visibleCategories.find((entry) => entry.id === ref.id)
    const ids = new Set(category?.itemOfferId ?? [])
    return catalog.visibleItemOffers.filter((offer) => ids.has(offer.id))
  }
  const products = catalog.items.filter(
    (item) => (ref.externalCode !== "" && item.externalCode === ref.externalCode) || (ref.id !== "" && item.id === ref.id),
  )
  const productIds = new Set(products.map((item) => item.id))
  return catalog.visibleItemOffers.filter((offer) => productIds.has(offer.itemId) && onMenu.has(offer.id))
}

/** The eligible menu products of an item benefit, in the order its refs list them, each offer once. */
export function prizeChoicesOf(refs: readonly CatalogRef[], catalog: PrizeCatalog): PrizeChoice[] {
  const onMenu = new Set(catalog.visibleCategories.flatMap((category) => category.itemOfferId ?? []))
  const seen = new Set<string>()
  const choices: PrizeChoice[] = []
  for (const ref of refs) {
    for (const offer of offersFor(ref, catalog, onMenu)) {
      const item = catalog.items.find((entry) => entry.id === offer.itemId)
      if (!item || seen.has(offer.id)) continue
      seen.add(offer.id)
      choices.push({ item, offer, needsOptions: needsOptions(offer, catalog.optionGroups) })
    }
  }
  return choices.slice(0, MAX_CHOICES)
}

/** How to spend a prize that gives `benefit` with today's menu. */
export function prizeOfferOf(benefit: CouponBenefit, catalog: PrizeCatalog): PrizeOffer {
  if (benefit.kind !== "items") return { kind: "use" }
  const choices = prizeChoicesOf(benefit.refs, catalog)
  if (choices.length === 0) return { kind: "menu" }
  if (choices.length === 1) return { kind: "add", choice: choices[0] }
  return { kind: "pick", choices }
}

/** The one call to action of a prize offer, in PT-BR. */
export function prizeOfferLabel(offer: PrizeOffer): string {
  switch (offer.kind) {
    case "use":
      return "Usar no meu pedido"
    case "add":
      return offer.choice.needsOptions ? "Escolher e adicionar à sacola" : "Adicionar à sacola"
    case "pick":
      return "Escolher meu prêmio"
    case "menu":
      return "Ver cardápio"
  }
}

/** Whether the bag already holds any of the products a free-item coupon can be spent on. */
export function freeItemInBag(offer: PrizeOffer, lines: readonly CartItem[]): boolean {
  const choices = offer.kind === "add" ? [offer.choice] : offer.kind === "pick" ? offer.choices : []
  return choices.some(({ item }) => lines.some((line) => refMatchesCartLine({ itemId: item.id, externalCode: item.externalCode }, line)))
}

/** What a free-items prize gives in words ("1 Sobremesa grátis" and what to choose), from its issued code's terms; null for any other prize or while the terms are unknown. */
export function prizeItemWords(terms: CouponTerms | null | undefined): { headline: string; detail: string | null } | null {
  if (terms?.offer?.kind !== "free-items") return null
  const words = couponBenefitHeadline(terms.benefit, terms.reward, 0, terms.offer)
  if (!words) return null
  return { headline: words.headline, detail: terms.offer.refs.length > 1 ? words.detail : null }
}
